import wsService from '@/services/embeds/plugin/ws.service'
import {
  useEmbedConfigureStore,
  type IComponentSettings
} from '@/stores/embeds/embedConfigure.store'
import { useIdeStore } from '@/stores/embeds/ide.store'
import { JDOODLEBLOCKSNAMES } from '@/utils/embeds/editorMeta'

/**
 * insert the value to the editor
 * @param value - the value to insert
 */
const insertOutputEditorSession = (value: string) => {
  if (!useIdeStore().outputEditor) return
  useIdeStore().outputEditor.insert(value.toString())
}
/**
 * Focus the editor
 */
const forcusOutputEditor = () => {
  if (!useIdeStore().outputEditor) return
  useIdeStore().outputEditor.focus()
}
/**
 * Get the program
 * @returns The program
 */
const getOutputEditorSession = () => {
  if (!useIdeStore().outputEditor) return ''
  return useIdeStore().outputEditor.getSession().getValue()
}
/**
 * Set the program
 * @param program - The program to set
 */
const outputEditorSetProgram = (program: string) => {
  if (!useIdeStore().outputEditor) return
  useIdeStore().outputEditor.getSession().setValue(program)
  heightChangeFunction()
}
/**
 * Sets font size of output editor
 * @param size - The size to set
 */
const changeFontSize = (size: number) => {
  if (!useIdeStore().outputEditor) return
  useIdeStore().outputEditor.setOptions({
    fontSize: `${size}px`
  })
}
/**
 * On height change function
 */
const heightChangeFunction = () => {
  const outputEditorRef = useIdeStore().outputEditorRef
  const editor = useIdeStore().outputEditor
  if (!editor || !outputEditorRef) return
  const minHeight = 128
  let lineHeight =
    editor.getSession().getScreenLength() * editor.renderer.lineHeight +
    editor.renderer.scrollBar.getWidth() +
    20
  if (lineHeight < minHeight) {
    lineHeight = minHeight
  }
  const modelHeight = useIdeStore().outputEditorModal
    ? parseInt(useIdeStore().outputEditorModal.getStyle().height)
    : minHeight
  const settongs = useEmbedConfigureStore().getComponentSettings?.find(
    (componentSetting: IComponentSettings) =>
      componentSetting.componentName === JDOODLEBLOCKSNAMES.OUTPUTCOMP
  )?.configStyle

  const autoGrow = settongs?.find((setting) => setting.id === 'autoGrow')?.value === 'true' || false
  const componentHeight = document.getElementById(JDOODLEBLOCKSNAMES.OUTPUTCOMP) || null
  if (autoGrow) {
    if (useIdeStore().outputEditorModal) {
      if (lineHeight !== modelHeight) {
        useIdeStore().outputEditorModal.setStyle({
          ...useIdeStore().outputEditorModal.getStyle(),
          height: `${lineHeight}px`
        })
        outputEditorRef.style.setProperty('height', `${lineHeight}px`)
      }
    } else if (componentHeight) {
      const parentComponent = componentHeight.parentElement as HTMLElement
      if (parentComponent) parentComponent.style.setProperty('height', `${lineHeight}px`)
      componentHeight.style.setProperty('height', `${lineHeight}px`)
      outputEditorRef.style.setProperty('height', `${lineHeight}px`)
    }
  } else {
    if (useIdeStore().outputEditorModal) {
      if (modelHeight < minHeight) {
        useIdeStore().outputEditorModal.setStyle({
          ...useIdeStore().outputEditorModal.getStyle(),
          height: `${minHeight}px`
        })
        outputEditorRef.style.setProperty('height', `${modelHeight}px`)
      } else if (lineHeight != modelHeight) {
        outputEditorRef.style.setProperty('height', `${modelHeight}px`)
      }
    } else {
      if (componentHeight) {
        const parentComponentHeight = componentHeight.parentElement?.clientHeight || minHeight
        componentHeight.style.setProperty('height', `${parentComponentHeight}px`)
        outputEditorRef.style.setProperty('height', `${parentComponentHeight}px`)
      }
    }
  }

  outputEditorRef.style.setProperty('min-height', `${minHeight}px`)
  editor.resize()
}

/**
 * On keyup code editor
 */
const onKeyupOutputEditor = () => {
  if (!useIdeStore().outputEditor) return
  useIdeStore()
    .outputEditor.textInput.getElement()
    .addEventListener('keyup', () => {
      heightChangeFunction()
    })
  useIdeStore()
    .outputEditor.textInput.getElement()
    .addEventListener('keypress', (event: KeyboardEvent) => {
      wsService.onKeyupOutputEditor(event)
    })
  useIdeStore()
    .outputEditor.textInput.getElement()
    .addEventListener('keydown', (event: KeyboardEvent) => {
      //if event is backspace or delete
      if (event.key === 'Backspace' || event.key === 'Delete') {
        wsService.onKeyupOutputEditor(event)
      }
    })
}

/**
 * Initialize the ace editor
 * @param outputEditor - The outputEditor element to initialize
 * @returns The ace editor
 */
const initAceEditor = (outputEditor: Element) => {
  window.ace.require('ace/ext/language_tools')
  window.ace.config.set('basePath', '/assets/javascript/ace')

  const editor: ace['Editor'] = window.ace.edit(outputEditor)
  editor.renderer.attachToShadowRoot()
  editor.getSession().setMode('ace/mode/text')
  editor.setTheme(`ace/theme/gruvbox`)
  editor.renderer.setShowGutter(false)
  editor.renderer.setAnimatedScroll(true)
  editor.renderer.setShowPrintMargin(false)
  editor.renderer.setPadding(20)
  editor.renderer.setScrollMargin(20, 20)
  editor.setDisplayIndentGuides(false)
  editor.setHighlightActiveLine(false)

  editor.setOptions({
    enableBasicAutocompletion: true,
    enableSnippets: true,
    enableLiveAutocompletion: false
  })
  editor.$blockScrolling = Infinity

  return editor
}
/**
 * Initialize the editor when router changes
 * @param element - The element to initialize
 * @param count - The count of the init
 * @returns null
 */
const initOutputEditor = async (element: HTMLElement, count: number = 0) => {
  if (!useIdeStore().isWindowAce()) {
    if (count > 10) {
      useIdeStore().setShareNotFound(true)
      useIdeStore().setShareNotFoundHttpError('Unable to load the Embeds. Please try again later.')
    } else {
      await new Promise((resolve) => setTimeout(resolve, 100))
      initOutputEditor(element, count + 1)
    }
  } else {
    if (useIdeStore().outputEditor) {
      destroyOutputEditor()
    }
    useIdeStore().outputEditorRef = element
    useIdeStore().outputEditor = initAceEditor(element)
    onSettingChange()
    await new Promise((resolve) => setTimeout(resolve, 100))
    heightChangeFunction()
    onKeyupOutputEditor()
  }
}
/**
 * On setting change
 */
const onSettingChange = () => {
  if (!useIdeStore().outputEditor) return
  const settongs = useEmbedConfigureStore().getComponentSettings?.find(
    (componentSetting: IComponentSettings) =>
      componentSetting.componentName === JDOODLEBLOCKSNAMES.OUTPUTCOMP
  )?.configStyle
  const fontSize = parseInt(settongs?.find((setting) => setting.id === 'fontSize')?.value) || 12
  changeFontSize(fontSize)
  heightChangeFunction()
}
/**
 * Set the code editor modal
 * @param modal - The modal to set
 */
const setModal = (modal: any) => {
  useIdeStore().outputEditorModal = modal
}
/**
 * Destroy the code editor
 */
const destroyOutputEditor = () => {
  if (!useIdeStore().outputEditor) return
  useIdeStore().outputEditor.destroy()
  useIdeStore().outputEditor = null
  useIdeStore().outputEditorRef = null
  useIdeStore().outputEditorModal = null
}

export default {
  setModal,
  initOutputEditor,
  onSettingChange,
  heightChangeFunction,
  forcusOutputEditor,
  insertOutputEditorSession,
  getOutputEditorSession,
  outputEditorSetProgram,
  destroyOutputEditor
}
